import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Button, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LogOut, Tablet } from 'lucide-react'
import { useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { EmptyState } from '../components/EmptyState'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { StatusChip } from '../components/StatusChip'
import { Guard, LoadState } from '../components/states'
import { ProblemNotice } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'

type Device = ApiSchemas['DeviceListResponse']['items'][number]

/** ADM-1: the Owner's device list; any device can be signed out remotely. */
export function DevicesScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'devices.manage')
  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => load(call(api.GET('/v1/staff/devices'))),
    enabled: allowed,
  })
  // A device signed out loses its button: the focus goes to what happened, not to the page.
  const [done, setDone] = useState<string | null>(null)

  return (
    <Guard allowed={allowed} title={m.devices.title}>
      <PageHeader title={m.devices.title} intro={m.devices.intro} />
      {done ? (
        <OutcomeNotice key={done}>
          <p>{format(m.devices.done, { name: done })}</p>
        </OutcomeNotice>
      ) : null}
      <LoadState data={devices.data} problem={problemOf(devices.error)}>
        {(data) =>
          data.items.length === 0 ? (
            <EmptyState icon={Tablet} title={m.devices.empty}>
              {m.devices.emptyHint}
            </EmptyState>
          ) : (
            <ul className="grid gap-6 md:grid-cols-2">
              {data.items.map((device) => (
                <DeviceCard key={device.id} device={device} onRevoked={setDone} />
              ))}
            </ul>
          )
        }
      </LoadState>
    </Guard>
  )
}

function DeviceCard({ device, onRevoked }: { device: Device; onRevoked: (name: string) => void }) {
  const { locale, messages: m } = useLocale()
  const { transition, takeOver } = useAccess()
  const queryClient = useQueryClient()
  const dialog = useRef<HTMLDialogElement>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)

  async function revoke() {
    setBusy(true)
    setProblem(null)
    const result = await call(
      api.POST('/v1/staff/devices/{deviceId}/revoke', {
        params: { path: { deviceId: device.id } },
      }),
    )
    setBusy(false)
    if (result.ok) {
      dialog.current?.close()
      if (device.current) {
        // This very device: the API has already dropped its cookies; the app drops
        // its credentials, the stored one too, as it moves to enrollment.
        transition({ state: 'enroll', reason: 'revoked' })
        return
      }
      onRevoked(device.name)
      await queryClient.invalidateQueries({ queryKey: ['devices'] })
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
  }

  return (
    <li>
      <div className="glass-pane flex h-full flex-col gap-2 rounded-card p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg">{device.name}</h2>
          {device.current ? <StatusChip tone="info">{m.devices.current}</StatusChip> : null}
          {device.revokedAt ? (
            <StatusChip tone="neutral">
              {format(m.devices.revoked, { date: formatDateTime(locale, device.revokedAt) })}
            </StatusChip>
          ) : null}
        </div>
        <p className="text-sm text-text-muted">
          {format(m.devices.enrolled, {
            name: device.enrolledBy,
            date: formatDateTime(locale, device.enrolledAt),
          })}
        </p>
        {device.revokedAt ? null : (
          <>
            {device.lastSeenAt ? (
              <p className="text-sm">
                {format(m.devices.lastSeen, { date: formatDateTime(locale, device.lastSeenAt) })}
              </p>
            ) : null}
            <p className="text-sm font-medium">
              {device.activeUser
                ? format(m.devices.inUse, { name: device.activeUser })
                : m.devices.locked}
            </p>
            <div className="mt-auto pt-2">
              <Button
                variant="staff"
                onClick={() => {
                  // A refusal from an earlier try is not this try's (OPS-1).
                  setProblem(null)
                  dialog.current?.showModal()
                }}
              >
                <LogOut aria-hidden="true" className="size-4" />
                {m.devices.revoke}
              </Button>
            </div>
          </>
        )}
      </div>
      <ConfirmDialog
        dialogRef={dialog}
        title={format(m.devices.revokeTitle, { name: device.name })}
        confirmLabel={m.devices.confirm}
        cancelLabel={m.devices.cancel}
        busy={busy}
        onConfirm={() => void revoke()}
        onClose={() => setProblem(null)}
      >
        <p>{m.devices.revokeBody}</p>
        {device.current ? (
          <Notice tone="warning">
            <p>{m.devices.revokeSelf}</p>
          </Notice>
        ) : null}
        <ProblemNotice problem={problem} />
      </ConfirmDialog>
    </li>
  )
}
